import * as THREE from 'three';
import { IndexedCache } from '../helpers/indexedcache';
import { TextureManager } from './texturemanager';
import { Mesh } from '../loaders/zms';
import { AnimationData } from '../loaders/zmo';
import { GeometryAnimator } from '../animators/geometryanimator';
import { MultiWait } from '../util/multiwait';
import { DataTable } from '../loaders/stb';
import { convertZnzinBlendType, convertZnzinBlendOp } from '../loaders/rose';

/*
  Note that pretty much everything in this file is purpose built for doing
  world morph animations such that all animations instantiated are always
  running and always play in sync (all instances of a morph object share the
  same underlying geometry.
 */

/**
 * Handles the games morph object list and handles the creation and cacheing
 * of all the related geometry and animations.
 *
 * @constructor
 */
export class MorphAnimManager {
  anims: any;
  cache: any;

  constructor() {
    this.anims = {};
    this.cache = new IndexedCache(this._loadOne.bind(this));
  }

  /**
   * @param {number} animIdx
   * @param {Function} callback
   * @private
   */
  _loadOne(animIdx: any, callback: any) {
    let animInfo = this.anims[animIdx];

    const texture = TextureManager.load(animInfo.texPath);
    const mat = new THREE.MeshBasicMaterial({color: 0xffffff, map: texture});

    if (animInfo.twoSided) {
      mat.side = THREE.DoubleSide;
    }
    if (animInfo.alphaTest) {
      mat.alphaTest = 0.5;
    }

    mat.transparent = true;
    mat.opacity = animInfo.alpha;
    mat.depthTest = animInfo.depthTest;
    mat.depthWrite = animInfo.depthWrite;
    mat.blending = THREE.CustomBlending;
    mat.blendEquation = convertZnzinBlendOp(animInfo.blendOp);
    mat.blendSrc = convertZnzinBlendType(animInfo.blendSrc);
    mat.blendDst = convertZnzinBlendType(animInfo.blendDst) as any;

    Mesh.load(animInfo.meshPath, function(geom: any) {
      AnimationData.load(animInfo.animPath, function(animData: any) {
        const anim = new GeometryAnimator(geom, animData);
        anim.play();

        if (callback) {
          callback({
            geom: geom,
            mat: mat,
            anim: anim
          });
        }
      });
    });
  }

  /**
   * @param {number} animIdx
   * @param {Function} callback
   * @returns {THREE.Object3D}
   */
  create(animIdx: any, callback: any) {
    const obj = new THREE.Object3D();

    this.cache.get(animIdx, function(data: any) {
      const mesh = new THREE.Mesh(data.geom, data.mat);
      obj.add(mesh);

      if (callback) {
        callback();
      }
    });

    return obj;
  }

  /**
   * Load helper so the MorphAnimManager can be controlled by the GDM.
   *
   * @param {string} path
   * Path to STB listing the morph objects
   * @param {Function} callback
   */
  static load(path: any, callback: any) {
    const data = new MorphAnimManager();

    const waitAll = new MultiWait();
    const dataTableWait = waitAll.one();
    DataTable.load(path, function(qdata: any) {
      for (let i = 0; i < qdata.rowCount; ++i) {
        const animInfo = qdata.row(i);
        if (animInfo[1] && animInfo[3]) {
          data.anims[i] = {
            meshPath: animInfo[1],
            animPath: animInfo[2],
            texPath: animInfo[3],
            alpha: parseFloat(animInfo[4]),
            twoSided: parseInt(animInfo[5]) !== 0,
            alphaTest: parseInt(animInfo[6]) !== 0,
            depthTest: parseInt(animInfo[7]) !== 0,
            depthWrite: parseInt(animInfo[8]) !== 0,
            blendSrc: parseInt(animInfo[9]),
            blendDst: parseInt(animInfo[10]),
            blendOp: parseInt(animInfo[11])
          };
        }
      }
      dataTableWait();
    });
    waitAll.wait(function() {
      callback(data);
    });
  }
}

